import { useEffect, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Lightbox } from './Lightbox';
import type { LightboxCaption, LightboxImageSize } from './Lightbox';

const magnoliaFlowerImages: [string, string] = [
  'https://assets.salieflewis.com/magnolia_flower/LewisSalief05.jpg',
  'https://assets.salieflewis.com/magnolia_flower/LewisSalief06.jpg',
];

const magnoliaFlowerCaptions: [LightboxCaption, LightboxCaption] = [
  {
    title: 'Magnolia Flower',
    year: '2026',
  },
  {
    title: 'Magnolia Flower (Detail)',
    year: '2026',
  },
];

const magnoliaFlowerImageSizes: [LightboxImageSize, LightboxImageSize] = [
  {
    width: 1999,
    height: 1332,
  },
  {
    width: 1999,
    height: 1332,
  },
];

function preloadImages(urls: string[]) {
  for (const url of urls) {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }
}

export function MagnoliaFlower() {
  const [show, setShow] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setShow(false);
    const timer = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const warmImages = () => preloadImages(magnoliaFlowerImages);

    if ('requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(warmImages);
      return () => window.cancelIdleCallback(idleCallback);
    }

    const timer = setTimeout(warmImages, 600);
    return () => clearTimeout(timer);
  }, []);

  const imageTrail = useTrail(1, {
    from: { opacity: 0 },
    to: show ? { opacity: 1 } : { opacity: 0 },
    config: { duration: 250 },
  });

  const handleImageClick = () => {
    setCurrentImageIndex(0);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setCurrentImageIndex(0);
  };

  return (
    <>
      <div className="relative z-1 flex w-full flex-col items-center justify-center gap-8 bg-transparent p-0 md:flex-row md:gap-4">
        <div className="flex max-w-full flex-col items-center justify-center gap-10 p-4 md:flex-row md:gap-8">
          <animated.button
            type="button"
            aria-label="View Magnolia Flower images"
            className="relative z-1 w-[min(82vw,640px)] shrink-0 origin-center cursor-pointer border-0 bg-transparent p-0 transition-transform duration-500 ease-out md:w-[min(62vw,760px)] md:hover:scale-[1.02]"
            style={imageTrail[0]}
            onClick={handleImageClick}
          >
            <img
              src={magnoliaFlowerImages[0]}
              alt={magnoliaFlowerCaptions[0].title}
              width={magnoliaFlowerImageSizes[0].width}
              height={magnoliaFlowerImageSizes[0].height}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="block h-auto w-full object-contain md:max-h-[min(58vh,680px)] xl:max-h-[720px]"
            />
          </animated.button>
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox
          images={magnoliaFlowerImages}
          caption={magnoliaFlowerCaptions[0]}
          captions={magnoliaFlowerCaptions}
          imageSizes={magnoliaFlowerImageSizes}
          imageClassName="md:max-w-[80vw] md:max-h-[80vh]"
          currentIndex={currentImageIndex}
          onClose={handleCloseLightbox}
          onNavigate={setCurrentImageIndex}
        />
      )}
    </>
  );
}
